// Phase 10B "AutoMind 2050" — Reports.
//
// Download CSVs and PDFs for any window. All download logic preserved.

import React, { useState } from 'react';
import { FileText, FileSpreadsheet, Phone, Calendar, Target, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { PageHeader } from '../components/ui/page-header';
import { DataCard } from '../components/ui/data-card';
import { SectionGrid } from '../components/ui/section-grid';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Reports = () => {
  const [loadingReport, setLoadingReport] = useState(null);
  const [dateRange, setDateRange] = useState('30');

  const downloadReport = async (type, format) => {
    const reportKey = `${type}-${format}`;
    setLoadingReport(reportKey);

    try {
      let url = `${BACKEND_URL}/api/reports/${type}/${format}`;
      if (type === 'calls') {
        url += `?days=${dateRange}`;
      }

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Failed to generate report');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${type}_report_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} ${format.toUpperCase()} downloaded`);
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Failed to download report');
    } finally {
      setLoadingReport(null);
    }
  };

  const reportTypes = [
    {
      id: 'calls',
      title: 'Call History',
      description: 'All call records with status, duration, outcomes, and transcripts.',
      icon: Phone,
      hasDateFilter: true
    },
    {
      id: 'appointments',
      title: 'Appointments',
      description: 'Scheduled appointments with customer details and status.',
      icon: Calendar,
      hasDateFilter: false
    },
    {
      id: 'campaigns',
      title: 'Campaign Performance',
      description: 'Campaign metrics including connect rates and conversions.',
      icon: Target,
      hasDateFilter: false
    }
  ];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <PageHeader
        eyebrow="Insights"
        title="Reports"
        description="Download CSVs and PDFs for any window."
      />

      <DataCard
        title="Settings"
        description="Default window for the call history export."
      >
        <div className="flex items-center gap-3">
          <label className="text-[10px] uppercase tracking-[0.16em] text-ink-500">
            Call history range
          </label>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            data-testid="date-range-select"
            className="bg-white/60 dark:bg-ink-900/40 border border-ink-200/60 dark:border-ink-800/60 rounded-md px-3 py-1.5 text-sm focus-2050"
          >
            <option value="7">Last 7 days</option>
            <option value="14">Last 14 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
            <option value="0">All time</option>
          </select>
        </div>
      </DataCard>

      <SectionGrid cols={3}>
        {reportTypes.map((report) => {
          const Icon = report.icon;
          return (
            <DataCard
              key={report.id}
              title={
                <span className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-ink-500" />
                  {report.title}
                </span>
              }
              description={report.description}
            >
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn-2050 btn-secondary flex-1"
                  onClick={() => downloadReport(report.id, 'csv')}
                  disabled={loadingReport === `${report.id}-csv`}
                  data-testid={`download-${report.id}-csv`}
                >
                  {loadingReport === `${report.id}-csv` ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="w-3.5 h-3.5" />
                  )}
                  CSV
                </button>
                <button
                  type="button"
                  className="btn-2050 btn-primary flex-1"
                  onClick={() => downloadReport(report.id, 'pdf')}
                  disabled={loadingReport === `${report.id}-pdf`}
                  data-testid={`download-${report.id}-pdf`}
                >
                  {loadingReport === `${report.id}-pdf` ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <FileText className="w-3.5 h-3.5" />
                  )}
                  PDF
                </button>
              </div>
            </DataCard>
          );
        })}
      </SectionGrid>

      <DataCard
        title="What's included"
        description="Fields included in each export."
      >
        <SectionGrid cols={3} stagger={false}>
          <div>
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <Phone className="w-3.5 h-3.5 text-gold" />
              Call History
            </h4>
            <ul className="text-xs text-ink-500 space-y-1">
              <li>Phone numbers and direction</li>
              <li>Duration and status</li>
              <li>Sentiment analysis</li>
              <li>Outcomes and appointments</li>
              <li>Voicemail detection</li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5 text-gold" />
              Appointments
            </h4>
            <ul className="text-xs text-ink-500 space-y-1">
              <li>Customer name and contact</li>
              <li>Date and time</li>
              <li>Appointment type</li>
              <li>Status tracking</li>
              <li>Reminder status</li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <Target className="w-3.5 h-3.5 text-gold" />
              Campaigns
            </h4>
            <ul className="text-xs text-ink-500 space-y-1">
              <li>Campaign name and type</li>
              <li>Target vs actual calls</li>
              <li>Connection rates</li>
              <li>Conversion rates</li>
              <li>Appointments booked</li>
            </ul>
          </div>
        </SectionGrid>
      </DataCard>
    </div>
  );
};

export default Reports;
